<template>
  <div class="tab-content">
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div>
          <h3 class="card-title">Fuso Horário da Obra</h3>
          <p class="card-desc">Referência oficial de horários da obra: notificações de rotinas, vencimentos e registros. Alteração somente manual.</p>
        </div>
      </div>

      <div class="tz-current-box">
        <div class="tz-current-info">
          <span class="field-label">Fuso atual</span>
          <span class="tz-current-name">{{ form.timezone || 'America/Sao_Paulo' }} <span class="tz-current-offset">({{ offsetOf(form.timezone || 'America/Sao_Paulo') }})</span></span>
        </div>
        <div class="tz-current-clock">{{ clockPreview }}</div>
      </div>

      <div class="tz-picker">
        <label class="field-label">Alterar fuso</label>
        <input v-model="filter" class="field-input" type="text" placeholder="Buscar fuso (ex: Sao_Paulo, New_York, Lisbon)..." />
        <select
          class="field-input tz-select"
          size="8"
          :value="form.timezone || 'America/Sao_Paulo'"
          @change="$emit('update-form', { field: 'timezone', value: ($event.target as HTMLSelectElement).value })"
        >
          <option v-for="tz in filteredZones" :key="tz" :value="tz">{{ tz }} ({{ offsetOf(tz) }})</option>
        </select>
        <p class="field-hint">O fuso da obra não muda sozinho — mesmo que a obra seja acessada de outros países, só é alterado aqui.</p>
      </div>

      <div class="save-row">
        <button class="btn-save" type="button" @click="$emit('save')">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          Salvar
        </button>
        <Transition name="fade"><span v-if="successText" class="save-success">{{ successText }}</span></Transition>
        <p v-if="errorText" class="save-error">{{ errorText }}</p>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, type PropType } from 'vue'
import type { SystemConfig } from '@/store/system'
import { listTimeZones, currentTimeInZone, zoneOffsetLabel } from '@/store/timezone'

export default defineComponent({
  name: 'SystemTabFuso',
  props: { form: { type: Object as PropType<SystemConfig>, required: true }, errorText: { type: String, default: '' }, successText: { type: String, default: '' } },
  emits: ['update-form', 'save'],
  setup(props) {
    const filter = ref('')
    const tick = ref(0)
    let timer: number | undefined

    const zones = listTimeZones()
    const filteredZones = computed(() => {
      const q = filter.value.trim().toLowerCase()
      const current = props.form.timezone || 'America/Sao_Paulo'
      const list = q ? zones.filter(tz => tz.toLowerCase().includes(q)) : zones
      // Garante que o fuso atual aparece na lista filtrada (mantém o select consistente)
      return list.includes(current) ? list : [current, ...list]
    })

    const clockPreview = computed(() => {
      void tick.value
      return currentTimeInZone(props.form.timezone || 'America/Sao_Paulo')
    })

    const offsetOf = (tz: string) => zoneOffsetLabel(tz)

    onMounted(() => { timer = window.setInterval(() => { tick.value++ }, 30_000) })
    onBeforeUnmount(() => { if (timer) window.clearInterval(timer) })

    return { filter, filteredZones, clockPreview, offsetOf }
  }
})
</script>

<style scoped lang="scss">
.tab-content { display: flex; flex-direction: column; gap: 16px; }
.settings-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card,#fff); padding: 22px 24px; display: flex; flex-direction: column; gap: 20px; }
.card-header { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 16px; border-bottom: 1px solid rgba(31,30,26,.07); }
.card-icon { width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0; background: rgba(31,30,26,.06); display: flex; align-items: center; justify-content: center; }
.card-title { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: var(--ink,#2c2b27); }
.card-desc { margin: 0; font-size: 12px; color: var(--ink-muted,#888); }
.field-label { font-size: 11px; font-weight: 700; color: var(--ink-muted,#888); text-transform: uppercase; letter-spacing: .05em; }
.field-hint { font-size: 11px; color: var(--ink-muted,#aaa); margin: 0; }
.field-input { padding: 9px 12px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.16); font-size: 14px; color: var(--ink,#2c2b27); background: #fff; outline: none; box-sizing: border-box; width: 100%; &:focus { border-color: var(--system-navbar,#0f4c81); } }

.tz-current-box {
  display: flex; align-items: center; justify-content: space-between; gap: 14px;
  padding: 14px 16px; border-radius: 10px;
  background: rgba(15,76,129,.05); border: 1px solid rgba(15,76,129,.15);
}
.tz-current-info { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.tz-current-name { font-size: 15px; font-weight: 700; color: var(--ink,#2c2b27); word-break: break-all; }
.tz-current-offset { font-size: 12px; font-weight: 600; color: var(--ink-muted,#888); }
.tz-current-clock { font-size: 26px; font-weight: 800; color: var(--system-navbar,#0f4c81); font-variant-numeric: tabular-nums; flex-shrink: 0; }

.tz-picker { display: flex; flex-direction: column; gap: 8px; max-width: 460px; }
.tz-select { padding: 4px; option { padding: 5px 8px; } }

.save-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.btn-save { display: inline-flex; align-items: center; gap: 8px; padding: 11px 24px; border-radius: 10px; border: none; cursor: pointer; background: var(--system-navbar,#0f4c81); color: #fff; font-size: 14px; font-weight: 700; &:hover { opacity: .88; } }
.save-success { font-size: 13px; font-weight: 600; color: #15803d; }
.save-error { margin: 0; font-size: 13px; color: #dc2626; }
.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
